import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatPoints } from '../utils/formatters';

/**
 * OfferRedemptionModal Component
 * Modal dialog that displays offer details and allows redemption
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Object} props.offer - The offer to display
 * @param {number} props.memberPoints - Current member points
 * @param {Function} props.onRedeem - Handler for redeeming the offer
 */
const OfferRedemptionModal = ({ open, onClose, offer, memberPoints, onRedeem }) => {
  if (!offer) return null;

  const hasEnoughPoints = memberPoints >= (offer.point_value || 0);
  const isOwned = !!offer.customer_coupon_id && (offer.point_value || 0) === 0;
  const canRedeem = isOwned || hasEnoughPoints;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      container={() => document.getElementById('root')}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '300px'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
        <Typography variant="h5" component="div">
          {offer.name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          {offer.description && (
            <Typography variant="body1" color="text.secondary" paragraph>
              {offer.description}
            </Typography>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {isOwned ? 'Ready to Redeem' : 'Points Required'}
            </Typography>
            {isOwned ? (
              <Typography variant="body1" color="primary.main">
                You already have this coupon and can redeem it now!
              </Typography>
            ) : (
              <>
                <Typography variant="h4" color="primary.main" gutterBottom>
                  {formatPoints(offer.point_value)} points
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your current balance: {formatPoints(memberPoints)} points
                </Typography>
                {!hasEnoughPoints && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    You need {formatPoints(offer.point_value - memberPoints)} more points to redeem this offer.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={onRedeem}
          variant="contained"
          disabled={!canRedeem}
        >
          {isOwned ? 'Redeem Coupon' :
           hasEnoughPoints ? 'Spend Points & Redeem' :
           'Not Enough Points'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferRedemptionModal;